import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight, GraduationCap } from 'lucide-react';
import { useRef } from 'react';
import { toast } from 'sonner';

const experts = [
  {
    id: 1,
    name: "Rahul Shah",
    role: "Equity Research Analyst",
    qualification: "CFA, MBA Finance",
    image: "https://images.unsplash.com/photo-1560250097-0b93528c311a?auto=format&fit=crop&q=80&w=400"
  },
  {
    id: 2,
    name: "Priya Mehta",
    role: "Technical Analyst",
    qualification: "CMT, CFTe",
    image: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&q=80&w=400"
  },
  {
    id: 3,
    name: "Vikram Singh",
    role: "Fund Manager",
    qualification: "CFA, FRM",
    image: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?auto=format&fit=crop&q=80&w=400"
  },
  {
    id: 4,
    name: "Ananya Sharma",
    role: "Derivatives Expert",
    qualification: "NISM Certified, MBA",
    image: "https://images.unsplash.com/photo-1580489944761-15a19d654956?auto=format&fit=crop&q=80&w=400"
  },
  {
    id: 5,
    name: "Deepak Verma",
    role: "Smallcap Specialist",
    qualification: "CA, CFA Level 2",
    image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&q=80&w=400"
  },
  {
    id: 6,
    name: "Neha Kapoor",
    role: "Macro Analyst",
    qualification: "PhD Economics, CFA",
    image: "https://images.unsplash.com/photo-1598550874175-4d0ef436c909?auto=format&fit=crop&q=80&w=400"
  }
];

const ExpertCard = ({ expert }) => {
  const handleFollow = () => {
    toast.success(`Following ${expert.name}!`, {
      description: "Coming Soon - You'll get their insights in your feed"
    });
  };

  return (
    <motion.div
      whileHover={{ y: -8, transition: { duration: 0.3 } }}
      className="hidden flex-shrink-0 w-[280px] bg-gradient-to-b from-[#1E293B] to-[#0F172A] rounded-2xl border border-[#334155] overflow-hidden"
      data-testid={`expert-card-${expert.id}`}
    >
      {/* Expert Image */}
      <div className="relative h-48 overflow-hidden">
        <img 
          src={expert.image} 
          alt={expert.name}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A] via-transparent to-transparent" />
      </div>

      {/* Expert Info */}
      <div className="p-5">
        <h3 className="text-[#F8FAFC] font-bold text-lg mb-1">{expert.name}</h3>
        <p className="text-[#94A3B8] text-sm mb-4">{expert.role}</p>

        <div className="flex items-center gap-2 mb-4">
          <GraduationCap size={16} className="text-[#22D3EE]" />
          <span className="text-[#94A3B8] text-sm">{expert.qualification}</span>
        </div>

        <button
          onClick={handleFollow}
          className="w-full py-2.5 bg-[#2563EB] hover:bg-[#2563EB]/90 text-white rounded-xl text-sm font-medium transition-all"
        >
          Follow Expert
        </button>
      </div>
    </motion.div>
  );
};

const ExpertDiscovery = () => {
  const scrollRef = useRef(null);

  const scroll = (direction) => {
    if (scrollRef.current) {
      const scrollAmount = 300;
      scrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  return (
    <section id="experts" className="py-20 md:py-32 relative" data-testid="expert-discovery-section">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="flex flex-col md:flex-row md:items-end justify-between mb-12"
        >
          <div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#F8FAFC] mb-4">
              Discover{' '}
              <span className="gradient-text">Verified Experts</span>
            </h2>
            <p className="text-[#94A3B8] text-base md:text-lg max-w-xl">
              Follow credible financial experts with proven qualifications. Get insights that matter.
            </p>
          </div>

          {/* Navigation Arrows */}
          <div className="flex gap-3 mt-6 md:mt-0">
            <button
              onClick={() => scroll('left')}
              className="w-12 h-12 rounded-full border border-[#334155] hover:border-[#2563EB] flex items-center justify-center text-[#94A3B8] hover:text-[#F8FAFC] transition-all"
              data-testid="experts-scroll-left"
            >
              <ChevronLeft size={20} />
            </button>
            <button
              onClick={() => scroll('right')}
              className="w-12 h-12 rounded-full border border-[#334155] hover:border-[#2563EB] flex items-center justify-center text-[#94A3B8] hover:text-[#F8FAFC] transition-all"
              data-testid="experts-scroll-right"
            >
              <ChevronRight size={20} />
            </button>
          </div>
        </motion.div>
      </div>

      {/* Horizontal Scrolling Cards */}
      <div className="relative">
        {/* Gradient overlays */}
        <div className="absolute left-0 top-0 bottom-0 w-20 bg-gradient-to-r from-[#0F172A] to-transparent z-10 pointer-events-none" />
        <div className="absolute right-0 top-0 bottom-0 w-20 bg-gradient-to-l from-[#0F172A] to-transparent z-10 pointer-events-none" />

        <div 
          ref={scrollRef}
          className="flex gap-6 overflow-x-auto pb-4 px-6 md:px-[calc((100vw-1280px)/2+24px)] hide-scrollbar scroll-smooth"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {experts.map((expert) => (
            <ExpertCard key={expert.id} expert={expert} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default ExpertDiscovery;
